/**
 * timeline — drei Quellen, eine Achse.
 *
 * Die Zeitachse beantwortet je Eintrag dieselben drei Fragen: **wann**, **wer**
 * (Auslöser) und **was** (Zustand als Kette in den neun festen Slots aus
 * `@/components/queue/stageChain`). Die Quellen sind verschieden, die Fragen
 * nicht — deshalb wird hier alles auf einen Eintragstyp gebracht, bevor
 * irgendetwas gezeichnet wird:
 *
 *   `runs.json`      → **Verlauf**  — was gelaufen ist (Build-Artefakt, immer da)
 *   `/api/queue`     → **Jetzt**    — was läuft und wartet (Live-Poll, 3 s)
 *   `/api/autoruns`  → **Geplant**  — was von selbst laufen wird (Vorschau)
 *
 * **Eine Achse, eine Richtung.** Nach unten wird die Zeit kleiner: ganz oben
 * das am weitesten Entfernte in der Zukunft, unten der älteste Lauf. Die Naht
 * zwischen „Geplant" und „Verlauf" ist damit exakt *jetzt* — und der als
 * Nächstes fällige Autorun steht unmittelbar über dem Jetzt-Band, dort wo er
 * hingehört. Drei Sortierungen mit je eigener Richtung hätten dieselbe
 * Information, aber keine Achse mehr.
 *
 * **Toter Runner ist der Normalfall.** Fehlen Queue und Autoruns (lokal
 * antworten beide mit 500), bleiben `current`/`upcoming` leer und der Verlauf
 * steht trotzdem vollständig. Das Modell kennt dafür keinen Sonderfall — es
 * bekommt schlicht `null` und lässt die beiden Bänder leer; die Seite sagt
 * dann *dort* Bescheid, wo die Live-Daten fehlen.
 */
import type { FlowCells } from "@/components/flow";
import { STAGE_SLOTS, runCells, jobCells } from "@/components/queue/stageChain";
import type { Job, QueueSnapshot } from "@/api/jobs";
import type { AutorunCfg } from "@/api/autoruns";
import type { Run } from "@/types";
import { describeSchedule, nextOccurrence } from "./schedule";

export type ActivityKind = "run" | "job" | "autorun";

/** Die drei Bänder der Achse — Zukunft, Gegenwart, Vergangenheit. */
export type ActivityBandId = "upcoming" | "current" | "past";

export interface ActivityEntry {
  key: string;
  kind: ActivityKind;
  band: ActivityBandId;
  /** Kurzwort vor dem Titel: „Run" · „Job" · „Autorun". */
  kindLabel: string;
  /** WANN — Sortzeit in ms. `null` = ohne Termin (abgeschalteter Autorun). */
  at: number | null;
  /** WER — Auslöser: `triggered_by`, Actor des Jobs oder der Zeitplan. */
  who: string;
  /** Rolle des Auslösers, ausgeschrieben („ausgelöst von" / „Zeitplan"). */
  whoRole: string;
  /** WAS — Titelzeile. */
  title: string;
  /** Stand (Formatversion), soweit der Eintrag einen kennt. */
  release: string | null;
  /** WAS — Zustand als Kette in den neun festen Slots. */
  cells: FlowCells;
  /** Ziel des Zeilenkopfs. Nur echte Links — Mittelklick muss tragen. */
  href?: string;
  /** Autorun: der rohe Cron-Ausdruck (Tooltip neben der Wortform). */
  raw?: string;
  /** Autorun: abgeschaltet — steht in der Liste, feuert aber nicht. */
  disabled?: boolean;
  /** Job: seine Id — das Ziel von `cancelJob`. */
  jobId?: string;
  /**
   * Job: darf abgebrochen werden. Wie in der abgelösten Queue-Seite genau für
   * laufende und wartende Jobs — `awaiting_user` wartet auf einen Menschen und
   * wird nicht abgebrochen, sondern beantwortet, und Fertiges ist fertig.
   */
  cancellable?: boolean;
}

export interface Timeline {
  upcoming: ActivityEntry[];
  current: ActivityEntry[];
  past: ActivityEntry[];
  /** Der als Nächstes fällige Autorun — die Antwort auf „was läuft von selbst". */
  nextUp: ActivityEntry | null;
}

export interface TimelineInput {
  runs: Run[];
  /** `null` = noch nicht geladen oder Runner offline. */
  queue: QueueSnapshot | null;
  /** `null` = noch nicht geladen oder Runner offline. */
  autoruns: AutorunCfg[] | null;
  now: number;
}

function ms(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  return Number.isFinite(t) ? t : null;
}

/** Ohne Termin heißt „unendlich weit vorn" — die Achse läuft nach unten rückwärts. */
function sortDesc(a: ActivityEntry, b: ActivityEntry): number {
  return (b.at ?? Number.POSITIVE_INFINITY) - (a.at ?? Number.POSITIVE_INFINITY);
}

/* ── Runs: der Verlauf ───────────────────────────────────────────────────── */

function runEntry(run: Run): ActivityEntry {
  return {
    key: `run:${run.id}`,
    kind: "run",
    band: "past",
    kindLabel: "Run",
    at: ms(run.trigger_date),
    who: run.triggered_by || "unbekannt",
    whoRole: "ausgelöst von",
    title: run.id,
    release: run.release ?? null,
    cells: runCells(run),
    href: `/runs/${run.id}`,
  };
}

/* ── Jobs: Gegenwart und die frisch fertigen ─────────────────────────────── */

/** Nur diese Rohzustände lassen sich abbrechen — übernommen aus `QueueTable`. */
const CANCELLABLE_STATES = new Set(["queued", "running"]);

function jobEntry(
  job: Job,
  run: Run | undefined,
  band: ActivityBandId,
  allowCancel = false,
): ActivityEntry {
  // Gegenwart: seit wann läuft/wartet er. Verlauf: wann war er fertig.
  const at =
    band === "past"
      ? (ms(job.finished_at) ?? ms(job.started_at) ?? ms(job.queued_at))
      : (ms(job.started_at) ?? ms(job.queued_at));
  return {
    key: `job:${job.id}`,
    kind: "job",
    band,
    kindLabel: "Job",
    at,
    who: job.actor || "unbekannt",
    whoRole: "eingereiht von",
    title: `${job.stage} — ${job.run_id}`,
    release: run?.release ?? null,
    cells: jobCells(job, run),
    href: `/runs/${job.run_id}`,
    jobId: job.id,
    cancellable: allowCancel && CANCELLABLE_STATES.has(job.state),
  };
}

/* ── Autoruns: die Vorschau nach vorn ────────────────────────────────────── */

/**
 * Die Kette eines Autoruns ist sein Bauplan: die Stages aus
 * `on_change.run_stages` stehen offen („noch nicht erreicht"), alles andere ist
 * Ghost („trifft nicht zu"). Damit ist auf denselben neun Slots ablesbar, was
 * dieser Autorun anfassen wird — und was nicht.
 */
export function autorunCells(cfg: AutorunCfg): FlowCells {
  const planned = new Set<string>(cfg.on_change?.run_stages ?? []);
  const cells: FlowCells = {};
  for (const slot of STAGE_SLOTS) {
    if (planned.has(slot.id)) cells[slot.id] = { state: "open" };
  }
  return cells;
}

function autorunEntry(cfg: AutorunCfg, now: number): ActivityEntry {
  const due = cfg.enabled ? nextOccurrence(cfg.schedule, now) : null;
  const words = describeSchedule(cfg.schedule);
  return {
    key: `autorun:${cfg.id}`,
    kind: "autorun",
    band: "upcoming",
    kindLabel: "Autorun",
    at: due ? due.getTime() : null,
    who: cfg.enabled ? words : `${words} — abgeschaltet`,
    whoRole: "Zeitplan",
    title: cfg.id,
    release: typeof cfg.probe?.scope?.release === "string" ? cfg.probe.scope.release : null,
    cells: autorunCells(cfg),
    raw: cfg.schedule,
    disabled: !cfg.enabled,
  };
}

/* ── Die Achse ───────────────────────────────────────────────────────────── */

export function buildTimeline({ runs, queue, autoruns, now }: TimelineInput): Timeline {
  const byId = new Map(runs.map((r) => [r.id, r]));

  const upcoming = (autoruns ?? []).map((a) => autorunEntry(a, now)).sort(sortDesc);

  // Abbrechbar sind laufende und wartende Jobs — dieselbe Trennung, die die
  // abgelöste Queue-Seite über ihre Tabs vorgenommen hat (`allowCancel`).
  const current = [
    ...(queue?.running ?? []).map((j) => jobEntry(j, byId.get(j.run_id), "current", true)),
    ...(queue?.waiting ?? []).map((j) => jobEntry(j, byId.get(j.run_id), "current", true)),
    ...(queue?.awaiting_user ?? []).map((j) => jobEntry(j, byId.get(j.run_id), "current", false)),
  ].sort(sortDesc);

  const past = [
    ...runs.map(runEntry),
    ...(queue?.recent ?? []).map((j) => jobEntry(j, byId.get(j.run_id), "past")),
  ].sort(sortDesc);

  // Der als Nächstes Fällige ist der unterste terminierte Eintrag des
  // Zukunftsbands — die Achse läuft nach unten auf das Jetzt zu.
  const dated = upcoming.filter((e) => e.at != null);
  const nextUp = dated.length > 0 ? dated[dated.length - 1] : null;

  return { upcoming, current, past, nextUp };
}

/* ── Zeit in Worten ──────────────────────────────────────────────────────── */

const ABSOLUTE = new Intl.DateTimeFormat("de-DE", { dateStyle: "medium", timeStyle: "short" });

/** Zeitpunkt ausgeschrieben — steht im Untertitel jeder Zeile. */
export function formatAbsolute(at: number | null): string {
  return at == null ? "ohne Termin" : ABSOLUTE.format(new Date(at));
}

const RELATIVE = new Intl.RelativeTimeFormat("de-DE", { numeric: "auto" });

const UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
  ["year", 365 * 24 * 3600_000],
  ["month", 30 * 24 * 3600_000],
  ["day", 24 * 3600_000],
  ["hour", 3600_000],
  ["minute", 60_000],
];

/**
 * Abstand zu jetzt — die rechte Spalte. „vor 3 Tagen" / „in 8 Stunden"
 * beantwortet die Frage „wann" schneller als ein Datum und macht die Richtung
 * der Achse ohne Legende lesbar.
 */
export function formatRelative(at: number | null, now: number): string {
  if (at == null) return "—";
  const delta = at - now;
  const abs = Math.abs(delta);
  for (const [unit, size] of UNITS) {
    if (abs >= size) return RELATIVE.format(Math.round(delta / size), unit);
  }
  return "gerade eben";
}
